window._paq = window._paq || [];
window._mtm = window._mtm || [];

export class MatomoManager {
  public containerId?: string;
  public siteId?: string;
  private analyticsConsent: boolean | null = null;
  private containerConsentRegistered = false;
  private directTrackerStarted = false;
  private containerTrackers = new Map<MatomoTracker, () => void>();
  constructor(
    public url: string,
    { containerId, siteId }: { containerId?: string; siteId?: string },
  ) {
    this.containerId = containerId;
    this.siteId = siteId;
  }
  loadMatomo() {
    // A Matomo Tag Manager container may include its own page-view tag. When a
    // container is configured it owns tracker startup, preventing a second
    // direct tracker from recording the same page view.
    if (!this.siteId || this.containerId) {
      return this;
    }
    window._paq.push(['requireCookieConsent']);
    this.directTrackerStarted = true;
    this.applyConsentToDirectTracker();
    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);
    var u = this.url;
    window._paq.push(['setTrackerUrl', u + 'matomo.php']);
    window._paq.push(['setSiteId', this.siteId]);
    var d = document,
      g = d.createElement('script'),
      s = d.getElementsByTagName('script')[0];
    g.async = true;
    g.src = u + 'matomo.js';
    s.parentNode!.insertBefore(g, s);
    return this;
  }
  loadMTM() {
    if (!this.containerId) {
      return this;
    }
    this.registerContainerConsent();
    window._mtm.push({
      'mtm.startTime': new Date().getTime(),
      event: 'mtm.Start',
    });
    var d = document,
      g = d.createElement('script'),
      s = d.getElementsByTagName('script')[0];
    g.async = true;
    g.src = `${this.url}js/container_${this.containerId}.js`;
    s.parentNode!.insertBefore(g, s);
    return this;
  }
  connectToConsentDialog() {
    const applyConsent = (categories: string[], emitEvent = true) => {
      const nextAnalyticsConsent = categories.includes('analytics');
      if (nextAnalyticsConsent === this.analyticsConsent) {
        return;
      }
      this.analyticsConsent = nextAnalyticsConsent;

      if (this.containerId && this.containerConsentRegistered) {
        this.containerTrackers.forEach((synchronize) => synchronize());
      } else if (!this.containerId && this.directTrackerStarted) {
        this.applyConsentToDirectTracker();
      }

      if (emitEvent) {
        window._mtm.push({
          event: nextAnalyticsConsent
            ? 'mtm.ConsentGiven'
            : 'mtm.ConsentRevoked',
        });
      }
    };

    window.addEventListener('cc:onConsent', ({ detail }) => {
      applyConsent(detail.cookie.categories);
    });
    window.addEventListener('cc:onChange', ({ detail }) => {
      if (detail.changedCategories.includes('analytics')) {
        applyConsent(detail.cookie.categories);
      }
    });
    // Restore the persisted choice without replaying a Tag Manager consent
    // event on every page view. TrackerSetup applies this state before the
    // container's page-view tag runs.
    applyConsent(window.CookieConsent.getCookie().categories || [], false);
    return this;
  }

  private applyConsentToDirectTracker() {
    if (this.analyticsConsent === null) {
      return;
    }
    window._paq.push([
      this.analyticsConsent
        ? 'setCookieConsentGiven'
        : 'forgetCookieConsentGiven',
    ]);
  }

  private registerContainerConsent() {
    if (this.containerConsentRegistered) {
      return;
    }
    this.containerConsentRegistered = true;

    // MTM configures trackers after TrackerSetup, then immediately tracks the
    // page. Synchronize its consent requirements synchronously so its first
    // request respects the dialog, even when Matomo remembers an older choice.
    const register = () => {
      if (!window.Matomo) {
        return;
      }
      const prepareTracker = (tracker: MatomoTracker) => {
        if (this.containerTrackers.has(tracker)) return;
        let internal = false;
        let cookiesDisabled = false;
        let featuresDisabled = false;
        let trackingConsentRequired = tracker.isConsentRequired();
        const original = {
          requireCookieConsent: tracker.requireCookieConsent.bind(tracker),
          requireConsent: tracker.requireConsent.bind(tracker),
          setConsentGiven: tracker.setConsentGiven.bind(tracker),
          forgetConsentGiven: tracker.forgetConsentGiven.bind(tracker),
          setCookieConsentGiven: tracker.setCookieConsentGiven.bind(tracker),
          forgetCookieConsentGiven:
            tracker.forgetCookieConsentGiven.bind(tracker),
          disableCookies: tracker.disableCookies.bind(tracker),
          disableBrowserFeatureDetection:
            tracker.disableBrowserFeatureDetection.bind(tracker),
          enableBrowserFeatureDetection:
            tracker.enableBrowserFeatureDetection.bind(tracker),
        };
        const synchronize = () => {
          internal = true;
          try {
            if (trackingConsentRequired) {
              if (this.analyticsConsent) original.setConsentGiven(false);
              else original.forgetConsentGiven();
            }
            if (this.analyticsConsent && !cookiesDisabled) {
              original.setCookieConsentGiven();
            } else {
              original.forgetCookieConsentGiven();
            }
          } finally {
            internal = false;
          }
        };
        // Matomo also disables cookies internally when consent is absent.
        // Only an explicit container setting must survive a later grant.
        tracker.disableCookies = () => {
          if (!internal) cookiesDisabled = true;
          original.disableCookies();
        };
        tracker.disableBrowserFeatureDetection = () => {
          if (!internal) featuresDisabled = true;
          original.disableBrowserFeatureDetection();
        };
        tracker.enableBrowserFeatureDetection = () => {
          if (!featuresDisabled) original.enableBrowserFeatureDetection();
        };
        tracker.setConsentGiven = (setCookieConsent) => {
          if (this.analyticsConsent) original.setConsentGiven(setCookieConsent);
        };
        tracker.setCookieConsentGiven = () => {
          if (this.analyticsConsent && !cookiesDisabled)
            original.setCookieConsentGiven();
        };
        tracker.requireCookieConsent = () => {
          if (internal) return original.requireCookieConsent();
          internal = true;
          let required;
          try {
            required = original.requireCookieConsent();
          } finally {
            internal = false;
          }
          synchronize();
          return required;
        };
        tracker.requireConsent = () => {
          if (internal) return original.requireConsent();
          trackingConsentRequired = true;
          internal = true;
          try {
            original.requireConsent();
          } finally {
            internal = false;
          }
          synchronize();
        };
        // Keep every TrackerSetup instance, including MTM configurations that
        // do not register their tracker in Matomo.getAsyncTrackers().
        this.containerTrackers.set(tracker, synchronize);
        tracker.requireCookieConsent();
      };
      window.Matomo.on('TrackerSetup', prepareTracker);
      window.Matomo.getAsyncTrackers().forEach(prepareTracker);
    };

    if (window.Matomo) {
      register();
      return;
    }
    window.matomoPluginAsyncInit = window.matomoPluginAsyncInit || [];
    window.matomoPluginAsyncInit.push(register);
  }
}

export default function matomo(
  url: string,
  {
    containerId,
    siteId,
  }: {
    containerId?: string;
    siteId?: string;
  },
) {
  return new MatomoManager(url, { containerId, siteId });
}
